/* ============================================================
   TERRAIN COLLECTION — terrain-collection.js
   Vanilla JS, no deps, no build. Handles:
     1. Smooth-scroll for in-page anchors ([data-tcol-scroll])
     2. Reveal-on-scroll for major sections
     3. Blend grid filter chips
     4. Quiz state machine (3 questions, 7 result mappings)
     5. Seeding calendar "today" indicator
     6. Optional GA4 events (guarded behind window.gtag check)
   ============================================================ */

(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function track(eventName, params) {
    if (typeof window.gtag === 'function') {
      try { window.gtag('event', eventName, params || {}); } catch (e) { /* noop */ }
    }
  }

  /* ---------- 1. Smooth-scroll anchors ---------- */
  function initScrollAnchors() {
    var links = document.querySelectorAll('[data-tcol-scroll]');
    links.forEach(function (link) {
      link.addEventListener('click', function (e) {
        var href = link.getAttribute('href') || '';
        if (!href.startsWith('#')) return;
        var target = document.querySelector(href);
        if (!target) return;
        e.preventDefault();
        var top = target.getBoundingClientRect().top + window.pageYOffset - 16;
        window.scrollTo({ top: top, behavior: 'smooth' });
      });
    });
  }

  /* ---------- 2. Reveal-on-scroll ---------- */
  function initScrollReveal() {
    document.body.classList.add('tcol-anim');
    var selectors = '.tcol-finder, .tcol-grid, .tcol-quiz, .tcol-cal, .tcol-semer, .tcol-compare, .tcol-trust, .tcol-quality, .tcol-faq, .tcol-brand';
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll(selectors).forEach(function (el) { el.classList.add('tcol-section--revealed'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('tcol-section--revealed');
          if (entry.target.classList.contains('tcol-cal')) {
            track('terrain_calendar_view', {});
          }
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.05, rootMargin: '0px 0px -8% 0px' });
    document.querySelectorAll(selectors).forEach(function (el) { io.observe(el); });
  }

  /* ---------- 3. Blend grid filter chips ---------- */
  function initBlendFilter() {
    var groups = document.querySelectorAll('[data-tcol-filter-group]');
    groups.forEach(function (group) {
      var chips = group.querySelectorAll('.tcol-grid__filter-chip');
      var listSelector = group.getAttribute('data-tcol-filter-target');
      var list = listSelector ? document.querySelector(listSelector) : null;
      if (!list) return;
      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          var code = chip.getAttribute('data-blend-code') || '';
          chips.forEach(function (c) { c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'); });
          list.querySelectorAll('.tcol-grid__card').forEach(function (card) {
            var cardCode = card.getAttribute('data-blend-code') || '';
            var hidden = code !== '' && code !== cardCode;
            card.setAttribute('data-hidden', hidden ? 'true' : 'false');
          });
        });
      });
    });
  }

  /* ---------- 4. Quiz state machine ----------
     The quiz reads three answers (sun, soil, intent) and walks a
     priority list to pick a blend code. Logic mirrors the mapping
     spelled out in the brief: first match wins, top-down.
  ----------------------------------------------- */
  function pickBlend(answers) {
    var sun  = answers.q1; /* "ombre-dense" | "mi-ombre" | "plein-soleil" */
    var soil = answers.q2; /* "normal" | "sec" | "pietine" */
    var goal = answers.q3; /* "nouveau" | "ameliorer" | "fairway" */

    if (sun === 'ombre-dense' || sun === 'mi-ombre') return 'OMBRE';
    if (soil === 'pietine') return 'VIVANT';
    if (soil === 'sec')     return 'ARIDE';
    if (goal === 'fairway') return 'GOLF';
    if (sun === 'plein-soleil' && soil === 'normal') return 'SOLEIL';
    return 'PRAIRIE';
  }

  function initQuiz() {
    var quiz = document.querySelector('[data-tcol-quiz]');
    if (!quiz) return;
    var steps = quiz.querySelectorAll('.tcol-quiz__step');
    var resultEl = quiz.querySelector('.tcol-quiz__result');
    var progressBar = quiz.querySelector('.tcol-quiz__bar-fill');
    var progressLabel = quiz.querySelector('[data-tcol-quiz-count]');
    var backBtn = quiz.querySelector('.tcol-quiz__back');
    var restartBtn = quiz.querySelector('.tcol-quiz__restart');
    var answers = { q1: null, q2: null, q3: null };
    var current = 0;
    var totalSteps = steps.length;
    var started = false;

    function showStep(idx) {
      steps.forEach(function (s, i) { s.setAttribute('data-active', i === idx ? 'true' : 'false'); });
      if (resultEl) resultEl.setAttribute('data-active', 'false');
      if (progressBar) progressBar.style.width = ((idx + 1) / totalSteps * 100) + '%';
      if (progressLabel) progressLabel.textContent = (idx + 1) + ' / ' + totalSteps;
      if (backBtn) backBtn.disabled = (idx === 0);
      current = idx;
    }

    function showResult() {
      var blend = pickBlend(answers);
      var resultBlocks = quiz.querySelectorAll('[data-tcol-quiz-result]');
      var matchedBlock = null;
      resultBlocks.forEach(function (block) {
        var match = block.getAttribute('data-tcol-quiz-result') === blend;
        block.style.display = match ? 'block' : 'none';
        if (match) matchedBlock = block;
      });
      if (!matchedBlock && resultBlocks.length > 0) {
        resultBlocks[resultBlocks.length - 1].style.display = 'block';
      }
      steps.forEach(function (s) { s.setAttribute('data-active', 'false'); });
      if (resultEl) {
        resultEl.setAttribute('data-active', 'true');
        var live = resultEl.querySelector('[aria-live]');
        if (live) {
          live.textContent = '';
          setTimeout(function () {
            if (matchedBlock) {
              var headline = matchedBlock.querySelector('.tcol-quiz__result-blend');
              if (headline) live.textContent = 'Votre mélange recommandé est ' + headline.textContent;
            }
          }, 50);
        }
      }
      if (progressBar) progressBar.style.width = '100%';
      track('terrain_quiz_complete', {
        q1_answer: answers.q1,
        q2_answer: answers.q2,
        q3_answer: answers.q3,
        recommended_blend: blend
      });
    }

    function handleOption(btn) {
      var step = btn.closest('.tcol-quiz__step');
      if (!step) return;
      var questionKey = step.getAttribute('data-tcol-question'); /* q1, q2, q3 */
      var value = btn.getAttribute('data-tcol-value');
      answers[questionKey] = value;
      var siblings = step.querySelectorAll('.tcol-quiz__option');
      siblings.forEach(function (s) { s.setAttribute('aria-pressed', s === btn ? 'true' : 'false'); });
      if (!started) {
        started = true;
        track('terrain_quiz_start', {});
      }
      setTimeout(function () {
        if (current < totalSteps - 1) {
          showStep(current + 1);
        } else {
          showResult();
        }
      }, 220);
    }

    quiz.querySelectorAll('.tcol-quiz__option').forEach(function (btn) {
      btn.addEventListener('click', function () { handleOption(btn); });
    });

    if (backBtn) {
      backBtn.addEventListener('click', function () {
        if (current > 0) showStep(current - 1);
      });
    }
    if (restartBtn) {
      restartBtn.addEventListener('click', function () {
        answers = { q1: null, q2: null, q3: null };
        started = false;
        quiz.querySelectorAll('.tcol-quiz__option').forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
        showStep(0);
      });
    }

    showStep(0);
  }

  /* ---------- 5. Calendar today indicator ----------
     Server-rendered; this is a defensive pass that re-applies the
     marker client-side using the visitor's local clock, in case the
     Liquid render date drifts from the visitor (e.g., cached HTML).
  -------------------------------------------------- */
  function initCalendarToday() {
    var cal = document.querySelector('[data-tcol-calendar]');
    if (!cal) return;
    var monthIdx = new Date().getMonth() + 1; /* 1..12 */
    cal.querySelectorAll('[data-month]').forEach(function (el) {
      var m = parseInt(el.getAttribute('data-month'), 10);
      if (m === monthIdx) el.setAttribute('data-today', 'true');
      else el.removeAttribute('data-today');
    });
  }

  /* ---------- 6. GA4 events on finder + Semer + restart ---------- */
  function initEventTracking() {
    document.querySelectorAll('[data-tcol-finder-card]').forEach(function (card) {
      card.addEventListener('click', function () {
        track('terrain_finder_click', {
          condition: card.getAttribute('data-condition') || '',
          recommended_blend: card.getAttribute('data-blend') || '',
          target_url: card.getAttribute('href') || ''
        });
      });
    });
    document.querySelectorAll('[data-tcol-semer-cta]').forEach(function (link) {
      link.addEventListener('click', function () {
        track('terrain_semer_cta_click', { target_url: link.getAttribute('href') || '' });
      });
    });
  }

  ready(function () {
    initScrollAnchors();
    initScrollReveal();
    initBlendFilter();
    initQuiz();
    initCalendarToday();
    initEventTracking();
  });
})();
